import { apiClient, ApiClient } from './client';
import type { 
  ScenarioDefinitionDetailResponse, 
  ScenarioDefinitionListResponse, 
  CreateScenarioDefinitionPayload, 
  ScenarioCharacter, 
  TemplateMessage,
  AddCharacterPayload,
  AddTemplateMessagePayload,
  ReorderMessagesPayload,
  UpdateScenarioDefinitionPayload,
  EditTemplateMessagePayload,
  RemoveCharacterPayload,
  SwapCharacterPayload,
  TemplateType,
  RemoveTemplateMessagePayload,
  AddAvailableCharacterPayload,
  RemoveAvailableCharacterPayload,
  Visibility
} from './types/scenario';

class ScenarioDefinition {
  public readonly id: string;
  private readonly apiClient: ApiClient;
  public readonly author: string;
  public display_name: string;
  public icon?: string;
  public tagline: string;
  public description: string;
  public characters: ScenarioCharacter[];
  public available_characters: ScenarioCharacter[];
  public messages: TemplateMessage[];
  public is_mature: boolean;
  public has_character_slot: boolean;
  public visibility: Visibility;

  constructor(id: string, apiClient: ApiClient, details: ScenarioDefinitionDetailResponse) {
    this.id = id;
    this.apiClient = apiClient;
    this.author = details.author;
    this.display_name = details.display_name;
    this.icon = details.icon;
    this.tagline = details.tagline;
    this.description = details.description;
    this.characters = details.characters;
    this.available_characters = details.available_characters;
    this.messages = details.messages;
    this.is_mature = details.is_mature;
    this.has_character_slot = details.has_character_slot;
    this.visibility = details.visibility;
  }

  async addCharacter(characterId: string): Promise<ScenarioDefinition> {
    try {
      const payload: AddCharacterPayload = { character: characterId };
      const response = await this.apiClient.post(`/scenario-definitions/${this.id}/add-character/`, payload);
      return new ScenarioDefinition(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to add character to scenario definition:', error);
      throw error;
    }
  }

  async addMessage(templateType: TemplateType, characterId: string, content: string): Promise<ScenarioDefinition> {
    try {
      const payload: AddTemplateMessagePayload = {
        template_type: templateType,
        character: characterId,
        content
      };
      const response = await this.apiClient.post(`/scenario-definitions/${this.id}/add-message/`, payload);
      return new ScenarioDefinition(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to add message to scenario definition:', error);
      throw error;
    }
  }

  async editMessage(messageId: string, updates: {
    templateType?: TemplateType,
    characterId?: string,
    content?: string
  }): Promise<ScenarioDefinition> {
    try {
      const payload: EditTemplateMessagePayload = {
        id: messageId
      };
      if (updates.templateType) payload.template_type = updates.templateType;
      if (updates.characterId) payload.character = updates.characterId;
      if (updates.content) payload.content = updates.content;

      const response = await this.apiClient.post(`/scenario-definitions/${this.id}/edit-message/`, payload);
      return new ScenarioDefinition(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to edit message in scenario definition:', error);
      throw error;
    }
  }

  async removeMessage(messageId: string): Promise<ScenarioDefinition> {
    try {
      const payload: RemoveTemplateMessagePayload = { id: messageId };
      const response = await this.apiClient.post(`/scenario-definitions/${this.id}/remove-message/`, payload);
      return new ScenarioDefinition(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to remove message from scenario definition:', error);
      throw error;
    }
  }

  async reorderMessages(messageIds: string[]): Promise<ScenarioDefinition> {
    try {
      const payload: ReorderMessagesPayload = { messages: messageIds };
      const response = await this.apiClient.post(`/scenario-definitions/${this.id}/reorder-messages/`, payload);
      return new ScenarioDefinition(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to reorder messages in scenario definition:', error);
      throw error;
    }
  }

  async update(data: UpdateScenarioDefinitionPayload): Promise<ScenarioDefinition> {
    try {
      const formData = new FormData();
      if (data.display_name) formData.append('display_name', data.display_name);
      if (data.tagline) formData.append('tagline', data.tagline);
      if (data.icon) formData.append('icon', data.icon);
      if (data.description) formData.append('description', data.description);
      if (data.visibility) formData.append('visibility', data.visibility);
      if (data.is_mature !== undefined) formData.append('is_mature', data.is_mature.toString());

      const response = await this.apiClient.patch(`/scenario-definitions/${this.id}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return new ScenarioDefinition(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to update scenario definition:', error);
      throw error;
    }
  }

  async removeCharacter(characterId: string): Promise<ScenarioDefinition> {
    try {
      const payload: RemoveCharacterPayload = { character: characterId };
      const response = await this.apiClient.post(`/scenario-definitions/${this.id}/remove-character/`, payload);
      return new ScenarioDefinition(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to remove character from scenario definition:', error);
      throw error;
    }
  }

  async swapCharacter(oldCharacterId: string, newCharacterId: string): Promise<ScenarioDefinition> {
    try {
      const payload: SwapCharacterPayload = {
        old_character: oldCharacterId,
        new_character: newCharacterId
      };
      const response = await this.apiClient.post(`/scenario-definitions/${this.id}/swap-character/`, payload);
      return new ScenarioDefinition(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to swap character in scenario definition:', error);
      throw error;
    }
  }

  async addAvailableCharacter(characterId: string): Promise<ScenarioDefinition> {
    try {
      const payload: AddAvailableCharacterPayload = { character: characterId };
      const response = await this.apiClient.post(`/scenario-definitions/${this.id}/add-available-character/`, payload);
      return new ScenarioDefinition(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to add available character to scenario definition:', error);
      throw error;
    }
  }

  async removeAvailableCharacter(characterId: string): Promise<ScenarioDefinition> {
    try {
      const payload: RemoveAvailableCharacterPayload = { character: characterId };
      const response = await this.apiClient.post(`/scenario-definitions/${this.id}/remove-available-character/`, payload);
      return new ScenarioDefinition(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to remove available character from scenario definition:', error);
      throw error;
    }
  }

  async delete(): Promise<void> {
    try {
      await this.apiClient.delete(`/scenario-definitions/${this.id}/`);
    } catch (error) {
      console.error('Failed to delete scenario definition:', error);
      throw error;
    }
  }

  async getScript(): Promise<string> {
    try {
      const response = await this.apiClient.get(`/scenario-definitions/${this.id}/script/`);
      return response.data.script;
    } catch (error) {
      console.error('Failed to fetch scenario script:', error);
      throw error;
    }
  }

  async updateFromScript(script: string): Promise<ScenarioDefinition> {
    try {
      const formData = new FormData();
      formData.append('script', script);
      
      const response = await this.apiClient.patch(`/scenario-definitions/${this.id}/script/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return new ScenarioDefinition(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to update scenario from script:', error);
      throw error;
    }
  }

  /**
   * Validates the scenario definition and refreshes metadata.
   * This ensures properties like `has_character_slot` are kept in sync with the current state.
   * Use this method when you need to ensure the scenario definition's metadata is up-to-date.
   * @returns A new ScenarioDefinition with refreshed metadata
   */
  async validate(): Promise<ScenarioDefinition> {
    try {
      const response = await this.apiClient.post(`/scenario-definitions/${this.id}/validate/`);
      return new ScenarioDefinition(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to validate scenario definition:', error);
      throw error;
    }
  }
}

export type ScenarioDefinitionFilter = 'trending' | 'featured' | 'top' | 'new';

export const scenarioDefinitions = {
  async list(): Promise<ScenarioDefinitionListResponse[]> {
    try {
      const response = await apiClient.get('/scenario-definitions/');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch scenario definitions:', error);
      throw error;
    }
  },

  async owned(): Promise<ScenarioDefinitionListResponse[]> {
    try {
      const response = await apiClient.get('/scenario-definitions/', {
        params: { owned: true }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch owned scenario definitions:', error);
      throw error;
    }
  },

  async create(payload: CreateScenarioDefinitionPayload): Promise<ScenarioDefinition> {
    try {
      const formData = new FormData();
      formData.append('description', payload.description);
      if (payload.icon) {
        formData.append('icon', payload.icon);
      }

      const response = await apiClient.post('/scenario-definitions/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return new ScenarioDefinition(response.data.id, apiClient, response.data);
    } catch (error) {
      console.error('Failed to create scenario definition:', error);
      throw error;
    }
  },

  async get(definitionId: string): Promise<ScenarioDefinition> {
    try {
      const response = await apiClient.get(`/scenario-definitions/${definitionId}/`);
      return new ScenarioDefinition(definitionId, apiClient, response.data);
    } catch (error) {
      console.error('Failed to fetch scenario definition details:', error);
      throw error;
    }
  },

  async filter(type: ScenarioDefinitionFilter): Promise<ScenarioDefinitionListResponse[]> {
    try {
      const response = await apiClient.get('/scenario-definitions/', {
        params: { filter: type }
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch ${type} scenario definitions:`, error);
      throw error;
    }
  },

  async search(query: string, exclude_mature: boolean = true): Promise<ScenarioDefinitionListResponse[]> {
    try {
      const response = await apiClient.get('/scenario-definitions/', {
        params: { 
          search: query,
          exclude_mature
        }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to search scenario definitions:', error);
      throw error;
    }
  }
};

export type { ScenarioDefinition as ScenarioDefinitionType };